/**
 * Porte-jetons : le seul endroit qui detient le jeton d'acces courant.
 *
 * POURQUOI CE MODULE MINUSCULE EXISTE
 *
 * Le client HTTP a besoin du jeton ; la couche d'authentification a besoin du
 * client HTTP pour rafraichir ce jeton. Les faire se referencer directement
 * creerait un cycle d'import. Ce module rompt le cycle : il ne depend de rien,
 * le client y LIT, la couche d'authentification y ECRIT.
 *
 * CHOIX DE STOCKAGE
 *
 * Le jeton d'ACCES vit uniquement en memoire : il disparait a la fermeture de
 * l'onglet et n'est jamais lisible par un script tiers via le stockage.
 *
 * Le jeton de RAFRAICHISSEMENT, lui, est persiste — sans quoi un simple F5
 * deconnecterait l'utilisateur. Il reste donc exposé a une faille XSS.
 * La parade correcte est un cookie `httpOnly`, mais elle suppose une evolution
 * de l'API, qui renvoie aujourd'hui ce jeton dans le corps de la reponse.
 * Limite connue, documentee au README.
 */

const CLE_RAFRAICHISSEMENT = 'astrolabe:rafraichissement';

let jetonAcces: string | null = null;
let rafraichir: (() => Promise<string | null>) | null = null;

/** Rafraichissement en cours, s'il y en a un. Voir `obtenirJetonFrais`. */
let rafraichissementEnCours: Promise<string | null> | null = null;

export function lireJetonAcces(): string | null {
  return jetonAcces;
}

export function ecrireJetonAcces(jeton: string | null): void {
  jetonAcces = jeton;
}

export function lireJetonRafraichissement(): string | null {
  try {
    return localStorage.getItem(CLE_RAFRAICHISSEMENT);
  } catch {
    // Stockage indisponible (navigation privee stricte) : la session ne survivra
    // pas au rechargement, mais l'application reste utilisable.
    return null;
  }
}

export function ecrireJetonRafraichissement(jeton: string | null): void {
  try {
    if (jeton === null) {
      localStorage.removeItem(CLE_RAFRAICHISSEMENT);
    } else {
      localStorage.setItem(CLE_RAFRAICHISSEMENT, jeton);
    }
  } catch {
    /* Ignore : voir ci-dessus. */
  }
}

/** Enregistre la fonction de rafraichissement. Appelee une fois, au demarrage. */
export function definirRafraichisseur(fonction: (() => Promise<string | null>) | null): void {
  rafraichir = fonction;
}

/**
 * Obtient un jeton d'acces frais apres un 401.
 *
 * DEDUPLICATION : si dix requetes echouent simultanement sur un jeton expire,
 * elles doivent partager UN SEUL rafraichissement. Sans cela, l'API recevrait dix
 * demandes concurrentes ; or elle fait tourner le jeton de rafraichissement a
 * chaque usage, donc neuf seraient rejetees comme des rejeux — et deconnecteraient
 * l'utilisateur alors que tout allait bien.
 */
export function obtenirJetonFrais(): Promise<string | null> {
  if (rafraichissementEnCours) {
    return rafraichissementEnCours;
  }

  if (!rafraichir) {
    return Promise.resolve(null);
  }

  rafraichissementEnCours = rafraichir().finally(() => {
    rafraichissementEnCours = null;
  });

  return rafraichissementEnCours;
}

/** Efface tout. Appelee a la deconnexion et sur echec de rafraichissement. */
export function viderJetons(): void {
  jetonAcces = null;
  ecrireJetonRafraichissement(null);
  rafraichissementEnCours = null;
}
